import React, { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import { InterstellarAccount, accountIsGeoreferenced } from '../api';
import type { AccountHealth } from '../api';
import { parseSipTrunk } from '../utils';

interface AccountListProps {
  accounts: InterstellarAccount[];
  selectedAccountId?: number | null;
  onAccountSelect: (account: InterstellarAccount) => void;
  healthMap?: Record<number, AccountHealth>;
  isLoading?: boolean;
  /** Relee el trunk desde el FreeSWITCH de la cuenta y lo guarda en Interstellar. */
  onRefreshSipTrunk?: (account: InterstellarAccount) => void;
  refreshingSipTrunkId?: number | null;
  /** Elimina la cuenta de Interstellar (no la infra de AWS). */
  onDeleteAccount?: (account: InterstellarAccount) => void;
  deletingAccountId?: number | null;
  /** Regenera XMLs de FreeSWITCH para cuentas compartidas. */
  onRegenerateFreeSwitch?: (account: InterstellarAccount) => void;
  regeneratingFreeSwitchId?: number | null;
}

export const AccountList: React.FC<AccountListProps> = ({
  accounts,
  selectedAccountId,
  onAccountSelect,
  healthMap = {},
  isLoading = false,
  onRefreshSipTrunk,
  refreshingSipTrunkId = null,
  onDeleteAccount,
  deletingAccountId = null,
  onRegenerateFreeSwitch,
  regeneratingFreeSwitchId = null,
}) => {
  const rows = useMemo(
    () => [...accounts].sort((a, b) => a.name.localeCompare(b.name)),
    [accounts]
  );

  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (openMenuId === null) return;
    const handleClickOutside = (): void => setOpenMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuId]);

  // The dashboard also moves the selection with j/k and the arrow keys.
  const selectedRowRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedAccountId]);

  if (isLoading && rows.length === 0) {
    return (
      <div className="flex h-full items-center justify-center bg-white-1.0 p-8 text-sm text-gray-2.0">
        Cargando cuentas…
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white-1.0">
      <div className="flex-1 min-h-0 overflow-auto">
        {rows.length === 0 ? (
          <div className="flex h-full items-center justify-center p-8 text-sm text-gray-2.0">
            No hay cuentas para mostrar.
          </div>
        ) : (
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10 border-b border-white-2.0 bg-white-1.0">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                  Cuenta
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                  Teléfono
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                  SIP Trunk
                </th>
                {onDeleteAccount && (
                  <th className="w-12 px-4 py-2.5 text-right text-xs font-bold uppercase tracking-wide text-gray-3.0">
                    <span className="sr-only">Acciones</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((account) => {
                const selected = account.id === selectedAccountId;
                const isRefreshing = refreshingSipTrunkId === account.id;
                const location = [account.city, account.state].filter(Boolean).join(', ');
                const { trunks } = parseSipTrunk(account.sip_trunk);
                return (
                  <tr
                    key={account.id}
                    ref={selected ? selectedRowRef : undefined}
                    tabIndex={0}
                    aria-selected={selected}
                    onClick={() => onAccountSelect(account)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onAccountSelect(account);
                      }
                    }}
                    className={classNames(
                      'cursor-pointer border-b border-white-2.0 transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-2.0',
                      selected
                        ? 'border-l-4 border-l-primary-2.0 bg-primary-2.0/10'
                        : account.freeswitch_group_id
                        ? 'bg-green-50 hover:bg-green-100'
                        : 'bg-white-0.0 hover:bg-white-1.0'
                    )}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={classNames(
                            'text-sm text-black-1.0',
                            selected ? 'font-bold' : 'font-semibold'
                          )}
                        >
                          {account.name}
                        </span>
                        {!accountIsGeoreferenced(account) && (
                          <span className="flex-shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                            Sin mapa
                          </span>
                        )}
                      </div>
                      {location && <p className="mt-0.5 text-xs text-gray-2.0">{location}</p>}
                    </td>
                    <td className="px-4 py-3">
                      {account.phone_number ? (
                        <span className="font-mono text-sm text-black-1.0">
                          {account.phone_number}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-2.0">—</span>
                      )}
                    </td>
                    <td className="max-w-xs px-4 py-3">
                      <div className="flex items-center gap-2">
                        {account.sip_trunk ? (
                          // Interstellar leads the stored summary with the trunk the
                          // account places its calls through, so the first entry is
                          // the one worth a column; the rest are named to whoever
                          // cannot reach a tooltip, and detailed in Enrutamiento.
                          <span
                            className="flex min-w-0 items-baseline gap-1.5"
                            title={account.sip_trunk}
                            aria-label={
                              trunks.length > 1
                                ? `${trunks.length} troncales: ${account.sip_trunk}`
                                : undefined
                            }
                          >
                            <span className="min-w-0 flex-1 truncate font-mono text-sm text-black-1.0">
                              {trunks[0]?.name ?? account.sip_trunk}
                            </span>
                            {trunks.length > 1 && (
                              <span className="flex-shrink-0 text-xs text-gray-2.0">
                                +{trunks.length - 1}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-2.0">Sin configurar</span>
                        )}
                        {onRefreshSipTrunk && !account.freeswitch_group_id && (
                          <button
                            type="button"
                            // The row itself selects the account, so the click must not
                            // reach it: refreshing is not a selection.
                            onClick={(e) => {
                              e.stopPropagation();
                              onRefreshSipTrunk(account);
                            }}
                            onKeyDown={(e) => e.stopPropagation()}
                            disabled={isRefreshing}
                            // Every row renders this button, so the account has to be
                            // part of its name for it to mean anything read aloud.
                            aria-label={`Actualizar SIP trunk de ${account.name}`}
                            aria-busy={isRefreshing}
                            title="Leer el trunk desde el FreeSWITCH de la cuenta"
                            className="flex-shrink-0 rounded border border-white-2.0 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-3.0 hover:bg-white-1.0 disabled:cursor-not-allowed"
                          >
                            {isRefreshing ? 'Leyendo…' : 'Actualizar'}
                          </button>
                        )}
                      </div>
                    </td>
                    {onDeleteAccount && (
                      <td className="px-4 py-3 text-right">
                        <div className="relative">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(openMenuId === account.id ? null : account.id);
                            }}
                            onKeyDown={(e) => e.stopPropagation()}
                            className={classNames(
                              'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                              openMenuId === account.id
                                ? 'bg-primary-2.0 text-white-0.0'
                                : 'bg-white-1.0 text-gray-3.0 hover:bg-white-2.0 hover:text-black-1.0'
                            )}
                            aria-label={`Acciones para ${account.name}`}
                          >
                            <Icon name={OrbitIcons.MoreVertical} size={18} />
                          </button>
                          {openMenuId === account.id && (
                            <div className="absolute right-0 top-full z-20 mt-1 w-52 overflow-hidden rounded-lg border border-white-2.0 bg-white-0.0 py-1 shadow-lg">
                              {onRegenerateFreeSwitch && account.freeswitch_group_id && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuId(null);
                                    onRegenerateFreeSwitch(account);
                                  }}
                                  disabled={regeneratingFreeSwitchId === account.id}
                                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-black-1.0 transition-colors hover:bg-white-1.0 disabled:cursor-not-allowed disabled:text-gray-2.0"
                                >
                                  <Icon
                                    name={OrbitIcons.RefreshCw}
                                    size={16}
                                    className={
                                      regeneratingFreeSwitchId === account.id ? 'animate-spin' : ''
                                    }
                                  />
                                  <span>
                                    {regeneratingFreeSwitchId === account.id
                                      ? 'Regenerando…'
                                      : 'Regenerar FreeSWITCH'}
                                  </span>
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuId(null);
                                  onDeleteAccount(account);
                                }}
                                disabled={deletingAccountId === account.id}
                                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:text-gray-2.0"
                              >
                                <Icon name={OrbitIcons.Trash} size={16} />
                                <span>
                                  {deletingAccountId === account.id
                                    ? 'Eliminando…'
                                    : 'Eliminar cuenta'}
                                </span>
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
